import React, { useState } from "react";
import {
    AGENTS,
    SAMPLE_QUESTIONS_PAGES,
    TOTAL_SAMPLE_PAGES,
} from "../constants";
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    SettingsIcon,
    WarningIcon,
} from "./Icons";

// ============================================================================
// SampleQuestions — paginated sample question cards for chatproxy_v4
// ============================================================================

export function SampleQuestions({ setInput }) {
    const [page, setPage] = useState(0);

    const currentPage = SAMPLE_QUESTIONS_PAGES[page];
    const gridCols =
        currentPage.columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2";

    return (
        <div className="w-full">
            <div className="flex items-center justify-center gap-2 mb-5">
                <h4 className="text-black dark:text-white text-center font-bold text-lg">
                    Sample Questions
                </h4>
            </div>

            <p className="text-black/80 dark:text-white/80 text-center text-sm mb-6">
                Click any question below to get started
            </p>

            <div className="relative flex items-center gap-4">
                <PaginationArrow
                    direction="left"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                />

                <div className="flex-1">
                    <div className={`grid grid-cols-1 ${gridCols} gap-4`}>
                        {currentPage.questions.map((q, idx) => (
                            <SampleQuestionCard
                                key={idx}
                                category={q.category}
                                text={q.text}
                                colSpan={q.colSpan}
                                onClick={() => setInput(q.text)}
                            />
                        ))}
                    </div>
                </div>

                <PaginationArrow
                    direction="right"
                    disabled={page === TOTAL_SAMPLE_PAGES - 1}
                    onClick={() =>
                        setPage((p) => Math.min(TOTAL_SAMPLE_PAGES - 1, p + 1))
                    }
                />
            </div>

            <div className="flex items-center justify-center mt-5">
                <div className="px-4 py-2 rounded-lg bg-orange-500/20 dark:bg-orange-500/30 border border-orange-500/40">
                    <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                        Page {page + 1} / {TOTAL_SAMPLE_PAGES}
                    </span>
                </div>
            </div>
        </div>
    );
}

function SampleQuestionCard({ category, text, colSpan, onClick }) {
    return (
        <button
            className={`group relative overflow-hidden text-left px-5 py-6 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 border border-orange-400/30 transition-all duration-300 text-white shadow-lg hover:shadow-2xl hover:shadow-orange-500/50 hover:-translate-y-1 ${
                colSpan === 2 ? "md:col-span-2" : ""
            }`}
            onClick={onClick}
        >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500" />
            <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-orange-100 uppercase tracking-wide">
                        {category}
                    </span>
                </div>
                <p className="font-medium text-sm leading-relaxed">{text}</p>
            </div>
        </button>
    );
}

function PaginationArrow({ direction, disabled, onClick }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`p-2 rounded-lg transition-all flex-shrink-0 ${
                disabled
                    ? "opacity-30 cursor-not-allowed"
                    : "hover:bg-white/10 dark:hover:bg-black/10"
            }`}
        >
            {direction === "left" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </button>
    );
}

// ============================================================================
// AgentSelector — dropdown for picking the active AI agent
// ============================================================================

export function AgentSelector({
    selectedAgent,
    setSelectedAgent,
    isDropdownOpen,
    setIsDropdownOpen,
    dropdownRef,
}) {
    const agentLabel = AGENTS[selectedAgent]?.label || "Agent";

    // Only show these agents in the dropdown (Gemini is currently hidden)
    const visibleAgents = ["chatproxy_v4", "chatproxy_NTUST"];

    return (
        <div className="relative w-full" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 bg-orange-500 dark:bg-white/10 hover:bg-orange-600 dark:hover:bg-white/20 text-white px-3 py-2 rounded-2xl border border-white/20 transition-all duration-200"
            >
                <SettingsIcon />
                <span className="text-sm font-medium">{agentLabel}</span>
                <ChevronDownIcon
                    className={`transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                />
            </button>

            {isDropdownOpen && (
                <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
                    <div className="py-1">
                        {visibleAgents.map((agentId) => {
                            const agent = AGENTS[agentId];
                            const isSelected = selectedAgent === agentId;

                            return (
                                <button
                                    key={agentId}
                                    onClick={() => {
                                        setSelectedAgent(agentId);
                                        setIsDropdownOpen(false);
                                    }}
                                    className={`w-full text-left px-4 py-3 hover:bg-orange-50 transition-colors ${
                                        isSelected ? "bg-orange-100" : ""
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={`w-2 h-2 rounded-full ${
                                                isSelected
                                                    ? "bg-orange-500"
                                                    : "bg-gray-300"
                                            }`}
                                        />
                                        <div>
                                            <div className="font-semibold text-gray-800">
                                                {agent.label}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {agent.description}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// ============================================================================
// GeminiAgentIntro — empty state for Gemini Agent
// ============================================================================

export function GeminiAgentIntro({ language }) {
    return (
        <div className="w-full text-center mb-2">
            <h3 className="text-white text-2xl font-bold mb-3">
                {language === "en"
                    ? "DocuSense Agent - Ask Anything!"
                    : "DocuSense Agent - 任何問題都可以問！"}
            </h3>
            <p className="text-slate-300 text-sm max-w-2xl mx-auto mb-2">
                {language === "en"
                    ? "This agent can answer questions on any topic, not limited to thermal interface materials. Upload images or documents to get detailed analysis and insights."
                    : "此 Agent 可以回答任何主題的問題，不限於熱介面材料。上傳圖片或文件以獲得詳細的分析和見解。"}
            </p>
            <div className="flex items-center justify-center gap-2 text-yellow-400 text-xs bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-2 max-w-2xl mx-auto">
                <WarningIcon />
                <span>
                    {language === "en"
                        ? "Disclaimer: Responses may not always be accurate. Please verify important information independently."
                        : "免責聲明：回應可能不完全準確。請獨立驗證重要資訊。"}
                </span>
            </div>
        </div>
    );
}

// ============================================================================
// NTUSTAgentIntro — empty state for NTUST Video Agent (language selection)
// ============================================================================

export function NTUSTAgentIntro({ setInput }) {
    return (
        <div className="w-full bg-gradient-to-br from-black/20 dark:from-white/20 to-black/10 dark:to-white/10 rounded-2xl p-6 backdrop-blur-md transition-all duration-300 shadow-2xl border border-white/20">
            <div className="flex items-center justify-center gap-2 mb-5">
                <h4 className="text-black dark:text-white text-center font-bold text-lg">
                    Language Settings
                </h4>
            </div>
            <p className="text-black/80 dark:text-white/80 text-center text-sm mb-6">
                Choose your preferred language to get started
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <LanguageCard
                    label="English"
                    description="Set to English response"
                    onClick={() => setInput("Hi")}
                />
                <LanguageCard
                    label="中文"
                    description="設定為中文回覆"
                    onClick={() => setInput("嗨")}
                />
            </div>
        </div>
    );
}

function LanguageCard({ label, description, onClick }) {
    return (
        <button
            className="group relative overflow-hidden text-left px-5 py-6 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 border border-orange-400/30 transition-all duration-300 text-white shadow-lg hover:shadow-2xl hover:shadow-orange-500/50 hover:-translate-y-1"
            onClick={onClick}
        >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500" />
            <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-orange-100 uppercase tracking-wide">
                        {label}
                    </span>
                </div>
                <p className="font-medium text-sm leading-relaxed">
                    {description}
                </p>
            </div>
        </button>
    );
}
